<template>
  <div class="medical-record-container">
    <van-nav-bar title="病例详情" left-arrow @click-left="goBack" class="page-title" />

    <el-form :model="form" :rules="rules" ref="formRef" label-width="100px" label-position="right" class="medical-form">
      <!-- 就诊医院 -->
      <el-form-item label="就诊医院:" prop="hospital">
        <el-input v-model="form.hospital" placeholder="请输入就诊医院"></el-input>
      </el-form-item>

      <!-- 患病名称 - 下拉框 -->
      <el-form-item label="患病名称:" prop="disease">
        <el-select v-model="form.disease" placeholder="请选择患病名称" class="full-width" filterable clearable>
          <el-option v-for="item in diseaseOptions" :key="item.key" :label="item.key" :value="item.key"></el-option>
        </el-select>
      </el-form-item>

      <!-- 提示信息 -->
      <el-form-item class="tips-item">
        <div class="tips-box">
          <span class="tips-icon">ℹ️</span>
          <p class="tips-content">
            <strong>患病名称提示：</strong><br />
            以上传染病可支持线上办理复课证明，其余传染病暂不能进行线上办理，请前往兴隆社区卫生服务中心预防接种科现场办理。
          </p>
        </div>
      </el-form-item>

      <!-- 日期选择 -->
      <el-form-item label="发病日期:" prop="onsetDate">
        <el-date-picker v-model="form.onsetDate" type="date" placeholder="选择发病日期" :picker-options="datePickerOptions"
          format="YYYY-MM-DD" value-format="YYYY-MM-DD" class="full-width" />
      </el-form-item>

      <el-form-item label="诊断日期:" prop="diagnosisDate">
        <el-date-picker v-model="form.diagnosisDate" type="date" placeholder="选择诊断日期"
          :picker-options="datePickerOptions" format="YYYY-MM-DD" value-format="YYYY-MM-DD" class="full-width" />
      </el-form-item>

      <!-- 主要症状 -->
      <el-form-item label="主要症状:" prop="symptomsStatus">
        <el-radio-group v-model="form.symptomsStatus" class="symptoms-radio-group" @change="handleSymptomsChange">
          <el-radio label="持续存在">持续存在</el-radio>
          <el-radio label="症状消失">症状消失</el-radio>
        </el-radio-group>
      </el-form-item>

      <!-- 动态表单区域 -->
      <div v-if="form.symptomsStatus === '持续存在'">
        <el-form-item label="症状详情:" prop="symptomsDetails">
          <el-input v-model="form.symptomsDetails" type="textarea" :rows="4" placeholder="请输入具体症状描述" />
        </el-form-item>
      </div>
      <div v-else-if="form.symptomsStatus === '症状消失'">
        <!-- 症状消失时间：日期 + 时间 -->
        <!-- 已移除 label-position="top" 和 class="vertical-layout-item" -->
        <el-form-item label="症状消失时间:" prop="disappearanceDate">
          <div class="date-time-picker">
            <el-date-picker v-model="form.disappearanceDate" type="date" placeholder="选择日期"
              :picker-options="datePickerOptions" format="YYYY-MM-DD" value-format="YYYY-MM-DD" class="date-picker" />
            <el-time-picker v-model="form.disappearanceTime" type="time" placeholder="选择时间" format="HH:mm:ss"
              value-format="HH:mm:ss" class="time-picker" id="disappearanceTime" />
          </div>
          <!-- 用于校验时间的隐藏表单项 -->
          <el-form-item prop="disappearanceTime" style="display: none;"></el-form-item>
        </el-form-item>
      </div>

      <!-- 提交按钮 -->
      <el-form-item class="action-buttons">
        <el-button type="primary" @click="handleSave" class="save-button" :loading="submissionLoading">保存</el-button>
      </el-form-item>
    </el-form>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, onBeforeUnmount, nextTick, watch } from 'vue';
import { useRouter } from 'vue-router';
import { ElMessage } from 'element-plus';
import request from '@/utils/request';

// 1. 路由实例
const router = useRouter();

// 2. 响应式数据
const formRef = ref(null);
const form = reactive({
  hospital: '',
  disease: '',
  onsetDate: '',
  diagnosisDate: '',
  symptomsStatus: '',
  symptomsDetails: '',
  disappearanceDate: '', // 日期
  disappearanceTime: ''  // 时间
});

const submissionLoading = ref(false);
const queId = ref('');
const diseaseOptions = ref([]);
const userInfo = ref(null);
const apiResponseData = ref({});

// 3. 自定义校验函数
const validateDateOrder = (rule, value, callback) => {
  if (typeof form.onsetDate === 'string' && typeof value === 'string' && form.onsetDate && value) {
    if (form.onsetDate > value) {
      callback(new Error('发病日期必须早于诊断日期'));
    } else {
      callback();
    }
  } else {
    callback();
  }
};

// 4. 表单校验规则
const rules = ref({
  hospital: [{ required: true, message: '请输入就诊医院', trigger: 'blur' }],
  disease: [{ required: true, message: '请选择患病名称', trigger: 'change' }],
  onsetDate: [{ required: true, message: '请选择发病日期', trigger: 'change' }],
  diagnosisDate: [
    { required: true, message: '请选择诊断日期', trigger: 'change' },
    { validator: validateDateOrder, trigger: 'change' }
  ],
  symptomsStatus: [{ required: true, message: '请选择主要症状状态', trigger: 'change' }],
  symptomsDetails: [{ required: true, message: '请输入具体症状描述', trigger: 'blur', transform: (value) => value?.trim() }],
  disappearanceDate: [
    { required: true, message: '请选择症状消失日期', trigger: 'change' }
  ],
  disappearanceTime: [
    { required: true, message: '请选择症状消失时间', trigger: 'change' }
  ]
});

// 5. 其他常量和配置
const datePickerOptions = {
  disabledDate(time) {
    return time.getTime() > Date.now();
  }
};

// 其他方法
const goBack = () => {
  router.go(-1);
};

const handleStorageChange = (e) => {
  if (e.key === 'userInfo' || e.key === 'apiResponseData') {
    loadUserAndApiData();
    nextTick(() => echoFormData());
  }
};

const loadUserAndApiData = () => {
  const userInfoStr = localStorage.getItem('userInfo');
  const apiResponseStr = localStorage.getItem('apiResponseData');

  if (apiResponseStr) {
    try {
      apiResponseData.value = JSON.parse(apiResponseStr);
    } catch (e) {
      console.error('解析 apiResponseData 失败:', e);
      localStorage.removeItem('apiResponseData');
      apiResponseData.value = {};
    }
  }

  if (userInfoStr) {
    try {
      userInfo.value = JSON.parse(userInfoStr);
      queId.value = apiResponseData.value.queId || userInfo.value.queId || '';
    } catch (e) {
      console.error('解析userInfo失败:', e);
      ElMessage.warning('加载用户信息失败，请重新登录');
      localStorage.removeItem('userInfo');
      router.push('/');
    }
  } else {
    ElMessage.warning('未检测到登录信息，请先登录');
    router.push('/');
  }
};

const loadDiseaseTypes = async () => {
  try {
    const response = await request({
      url: '/api/questionnaires/diseaseTypes',
      method: 'get',
    });

    if (response.code === 200 && typeof response.data === 'object' && response.data !== null) {
      diseaseOptions.value = Object.entries(response.data).map(([key, value]) => ({
        key: key,
        label: key
      }));
    } else {
      throw new Error('返回的数据格式不正确');
    }
  } catch (e) {
    console.error('获取患病类型失败:', e);
    ElMessage.warning('获取患病类型失败，使用默认选项');
    diseaseOptions.value = [
      { key: '流行性感冒', label: '流行性感冒' },
      { key: '水痘', label: '水痘' },
      { key: '手足口病', label: '手足口病' },
      { key: '疱疹性咽峡炎', label: '疱疹性咽峡炎' },
      { key: '流行性腮腺炎', label: '流行性腮腺炎' },
      { key: '其他感染性腹泻', label: '其他感染性腹泻' }
    ];
  }
};

const echoFormData = () => {
  form.hospital = apiResponseData.value.queHospital || userInfo.value?.queHospital || '';
  form.disease = apiResponseData.value.queDisease || userInfo.value?.queDisease || '';
  form.onsetDate = apiResponseData.value.queFallTime || userInfo.value?.queFallTime || '';
  form.diagnosisDate = apiResponseData.value.queDiagnosisTime || userInfo.value?.queDiagnosisTime || '';
  form.symptomsStatus = apiResponseData.value.queSymptoms || userInfo.value?.queSymptoms || '';
  form.symptomsDetails = apiResponseData.value.queSymptomsOther || userInfo.value?.queSymptomsOther || '';

  const rawDisappearanceDateTime = apiResponseData.value.queSymptomsTime || userInfo.value?.queSymptomsTime || '';
  if (rawDisappearanceDateTime) {
    const [date, time] = rawDisappearanceDateTime.split(' ');
    form.disappearanceDate = date || '';
    form.disappearanceTime = time || '';
  } else {
    form.disappearanceDate = '';
    form.disappearanceTime = '';
  }

  nextTick(() => {
    if (form.symptomsStatus === '持续存在') {
      // 清除时间相关校验
      rules.value.disappearanceDate = [];
      rules.value.disappearanceTime = [];
    } else if (form.symptomsStatus === '症状消失') {
      // 恢复时间相关校验
      rules.value.disappearanceDate = [
        { required: true, message: '请选择症状消失日期', trigger: 'change' }
      ];
      rules.value.disappearanceTime = [
        { required: true, message: '请选择症状消失时间', trigger: 'change' }
      ];
    }
  });
};

const handleSymptomsChange = (val) => {
  formRef.value?.clearValidate();
  if (val === '持续存在') {
    form.disappearanceDate = '';
    form.disappearanceTime = '';
    rules.value.disappearanceDate = [];
    rules.value.disappearanceTime = [];
    rules.value.symptomsDetails = [
      { required: true, message: '请输入具体症状描述', trigger: 'blur', transform: (value) => value?.trim() }
    ];
  } else if (val === '症状消失') {
    form.symptomsDetails = '';
    rules.value.symptomsDetails = [];
    rules.value.disappearanceDate = [
      { required: true, message: '请选择症状消失日期', trigger: 'change' }
    ];
    rules.value.disappearanceTime = [
      { required: true, message: '请选择症状消失时间', trigger: 'change' }
    ];
  }
};

const handleSave = async () => {
  if (!formRef.value) return;
  try {
    await formRef.value.validate();
    submissionLoading.value = true;

    if (!userInfo.value) {
      ElMessage.error('用户信息缺失，无法保存');
      return;
    }

    // 合并日期和时间
    const disappearanceDateTime = form.disappearanceDate && form.disappearanceTime
      ? `${form.disappearanceDate} ${form.disappearanceTime}`
      : '';

    const updatedUserInfo = {
      ...userInfo.value,
      ...apiResponseData.value,
      queHospital: form.hospital,
      queDisease: form.disease,
      queFallTime: form.onsetDate,
      queDiagnosisTime: form.diagnosisDate,
      queSymptoms: form.symptomsStatus,
      queSymptomsOther: form.symptomsDetails,
      queSymptomsTime: disappearanceDateTime, // 存储合并后的值
      queId: queId.value
    };

    localStorage.setItem('userInfo', JSON.stringify(updatedUserInfo));
    userInfo.value = updatedUserInfo;

    localStorage.setItem('bingliFormData', JSON.stringify(form));
    localStorage.setItem('bingliCompleted', 'true');

    ElMessage.success('病例信息保存成功！');
    router.push('/survey');
  } catch (error) {
    console.error('保存失败:', error);
    ElMessage.error('请填写完整所有必填项，并检查输入是否符合要求');
  } finally {
    submissionLoading.value = false;
  }
};

// 7. 生命周期钩子
onMounted(() => {
  loadUserAndApiData();
  loadDiseaseTypes();
  window.addEventListener('storage', handleStorageChange);

  watch([() => apiResponseData.value, () => userInfo.value], () => {
    if (apiResponseData.value || userInfo.value) {
      echoFormData();
    }
  }, { immediate: true, deep: true });
});

onBeforeUnmount(() => {
  window.removeEventListener('storage', handleStorageChange);
});
</script>

<script>
export default {
  name: 'Bingli'
}
</script>

<style scoped>
/* --- 基础与容器样式 --- */
.medical-record-container {
  padding: 16px;
  background-color: #f9f9f9;
  min-height: 100vh;
  box-sizing: border-box;
  position: relative;
  z-index: 1;
}

.page-title {
  font-size: 20px;
  color: #333;
  margin-bottom: 20px;
  padding-bottom: 10px;
  border-bottom: 1px solid #e9e9e9;
  text-align: center;
}

.medical-form {
  background-color: #fff;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  position: relative;
  z-index: 2;
}

/* --- 通用组件样式 --- */
.full-width {
  width: 100%;
}

/* --- 主要症状单选框组 --- */
.symptoms-radio-group {
  display: flex;
  align-items: center;
  gap: 20px;
  /* 你可以根据需要调整这个间距，比如 30px */
}

.symptoms-radio-group .el-radio {
  display: flex;
  align-items: center;
}

.symptoms-radio-group .el-radio__input {
  margin-right: 5px;
}

/* --- 提示信息框 --- */
.tips-item .el-form-item__content {
  margin-left: 0 !important;
}

.tips-box {
  background-color: #f5fafe;
  border-left: 4px solid #409eff;
  padding: 15px;
  border-radius: 4px;
  margin-bottom: 20px;
}

.tips-icon {
  color: #409eff;
  font-weight: bold;
  margin-right: 8px;
}

.tips-content {
  color: #666;
  line-height: 1.6;
  margin: 0;
}

/* --- 按钮区域 --- */
.action-buttons {
  margin-top: 30px;
  display: flex;
  justify-content: center;
  gap: 15px;
}

.save-button {
  min-width: 120px;
}

/* 日期时间选择器样式 */
.date-time-picker {
  display: flex;
  gap: 10px;
}

.date-picker,
.time-picker {
  flex: 1;
}

/* --- 响应式布局：移动端适配 --- */
@media (max-width: 768px) {
  .medical-form {
    padding: 15px;
  }

  .el-form {
    label-position: top;
    label-width: auto;
  }

  /* 在移动端，你可能仍然希望它们垂直排列，所以可以在这里覆盖 */
  .symptoms-radio-group {
    flex-direction: column;
    /* 在小屏幕上恢复垂直排列 */
    align-items: flex-start;
    /* 左对齐 */
    gap: 10px;
  }

  .action-buttons {
    flex-direction: column;
    align-items: stretch;
  }

  .action-buttons .el-button {
    width: 100%;
  }

  .date-time-picker {
    flex-direction: column;
    gap: 15px;
  }

  .vertical-layout-item .el-form-item__label {
    /* 增加 label 与输入区之间的距离 */
    margin-bottom: 10px !important;
    padding-right: 0 !important;
    /* 清除右侧内边距 */
    text-align: left !important;
    /* 让文字左对齐 */
  }
}
</style>